import { useEffect, useRef, type ReactNode, type RefObject } from "react";
import { Check, Warehouse } from "lucide-react";
import { RangeLine, gapTone } from "@/components/brand/parts";
import { chamfer } from "@/components/brand/Press";

// The feature bento: one tile per thing Octerm answers, each with a small picture of the answer.
// Every figure is sample data; competitor names are invented.

const GOLD = "#c9962b";

/** A tile: a title that says what you get, a line under it, then the picture. */
export function Tile({ id, title, desc, className = "", index, children }: {
  id?: string;
  title: string;
  desc: string;
  className?: string;
  index: number;
  children: ReactNode;
}) {
  return (
    <div
      id={id}
      className={`bento-tile scroll-mt-24 ${className}`}
      style={{ transitionDelay: `${(index % 3) * 90}ms` }}
    >
      {/* Drawn like the signup bar: a hairline edge, small chamfers and a lip along the bottom. */}
      <div className="h-full bg-slate-200 p-px" style={{ clipPath: chamfer(6) }}>
        <div className="flex h-full flex-col bg-white p-6 shadow-[inset_0_-4px_0_#eef2f5]" style={{ clipPath: chamfer(5.6) }}>
          <h3 className="text-balance text-xl font-semibold leading-snug tracking-tight text-slate-900">{title}</h3>
          <p className="mt-1.5 text-pretty text-sm leading-relaxed text-slate-600">{desc}</p>
          <div className="mt-6 flex flex-1 flex-col justify-end">{children}</div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rates, all-in: each competitor's street rate, and what a tenant pays once the promotion, the admin
// fee and insurance are counted, averaged over a six-month stay.

const STAY = 6;

interface Offer {
  name: string;
  street: number;
  promo: string | null;
  admin: number;
  insurance: number;
}

/** The average monthly cost of a six-month stay: rent after the promotion, plus the one-time admin
 *  fee spread over the stay, plus monthly insurance. */
function allIn({ street, promo, admin, insurance }: Offer) {
  const rent = promo === "First month $1" ? street * (STAY - 1) + 1 : promo === "50% off 2 mo" ? street * (STAY - 1) : promo === "Second month free" ? street * (STAY - 1) : street * STAY;
  return Math.round(rent / STAY + admin / STAY + insurance);
}

const RATES = [
  { name: "Harborline Self Storage", color: "#06b6d4", street: 158, promo: "50% off 2 mo", admin: 29, insurance: 12 },
  { name: "Ridgeview Storage", color: "#8b5cf6", street: 164, promo: "First month $1", admin: 25, insurance: 10 },
  { name: "Northgate Self Storage", color: "#f97316", street: 176, promo: "Second month free", admin: 0, insurance: 14 },
  { name: "Cedar Park Storage Co.", color: "#22c55e", street: 149, promo: null, admin: 35, insurance: 15 },
  { name: "Summit Lock & Store", color: "#031332", street: 171, promo: null, admin: 20, insurance: 9 },
]
  .map((r) => ({ ...r, total: allIn(r) }))
  .sort((a, b) => a.total - b.total);
const OURS: Offer = { name: "Your facility", street: 159, promo: null, admin: 22, insurance: 11 };
const OURS_TOTAL = allIn(OURS);
const median = (v: number[]) => {
  const s = [...v].sort((a, b) => a - b);
  return s.length % 2 ? s[(s.length - 1) / 2]! : (s[s.length / 2 - 1]! + s[s.length / 2]!) / 2;
};
const MARKET_TOTAL = median(RATES.map((r) => r.total));
const MARKET_STREET = median(RATES.map((r) => r.street));
const pctVs = (ours: number, them: number) => Math.round(((ours - them) / them) * 100);
const signed = (v: number) => `${v > 0 ? "+" : ""}${v}%`;

/** The operator's mark, as in the app: its initial in its colour, in a square. */
function OperatorMark({ name, color }: { name: string; color: string }) {
  return (
    <span className="flex h-4 w-4 shrink-0 items-center justify-center border-[1.5px] bg-white text-[9px] font-bold leading-none" style={{ borderColor: color, color }}>
      {name.charAt(0)}
    </span>
  );
}

const SECTION = "text-[10px] font-medium uppercase tracking-wider text-slate-400";
const LIST_ROW = "grid grid-cols-[minmax(0,1fr)_44px_52px] items-center gap-3 sm:grid-cols-[minmax(0,1fr)_44px_44px_52px]";

function RatesVisual() {
  const domain: [number, number] = [140, 190];
  const at = (v: number) => `${((v - domain[0]) / (domain[1] - domain[0])) * 100}%`;
  const allInGap = pctVs(OURS_TOTAL, MARKET_TOTAL);
  const streetGap = pctVs(OURS.street, MARKET_STREET);
  const terms = (r: Offer) => [r.promo, r.admin ? `$${r.admin} admin` : null, `$${r.insurance}/mo insurance`].filter(Boolean).join(", ");
  return (
    <div className="flex flex-1 flex-col">
      {/* The answer, as the app gives it: where you stand once everything a tenant pays is counted. */}
      <div className="flex items-baseline gap-2">
        <span className="px-1.5 text-[34px] font-semibold leading-tight tabular-nums text-slate-900" style={{ backgroundColor: gapTone(allInGap) }}>{allInGap}%</span>
        <span className="text-lg text-slate-700">above market, all-in</span>
      </div>
      <p className="mt-1.5 text-sm text-slate-600">
        <span className="font-semibold tabular-nums text-logo">${OURS_TOTAL}</span> you · <span className="font-semibold tabular-nums text-slate-900">${MARKET_TOTAL}</span> market. On street rates alone you'd look {Math.abs(streetGap)}% {streetGap < 0 ? "below" : "above"}.
      </p>

      {/* Every competitor on one line, all-in: their squares, the market median, and your diamond. */}
      <div className="relative mx-1 mt-6 h-[13px]">
        <div className="absolute inset-x-0 top-[5px] h-[3px] bg-slate-200" />
        <div className="absolute top-0 -ml-px h-[13px] w-[2px] bg-slate-500" style={{ left: at(MARKET_TOTAL) }} />
        {RATES.map((r) => (
          <span key={r.name} className="absolute top-[3px] -ml-[3.5px] h-[7px] w-[7px]" style={{ left: at(r.total), backgroundColor: r.color }} />
        ))}
        <span className="absolute top-[1px] -ml-[5.5px] h-[11px] w-[11px] rotate-45 border-2 border-white bg-logo" style={{ left: at(OURS_TOTAL) }} />
      </div>

      <div className={`${LIST_ROW} -mx-3 mt-6 border-b border-slate-200 px-3 pb-1.5 ${SECTION}`}>
        <span>Competitors · 10x10</span>
        <span className="hidden text-right sm:block">Street</span>
        <span className="text-right">All-in</span>
        <span className="text-right">Vs them</span>
      </div>
      <div className="flex flex-1 flex-col">
        {/* Everyone in price order, you included, where your all-in price puts you. */}
        {[...RATES.map((r) => ({ ...r, ours: false })), { ...OURS, total: OURS_TOTAL, color: "", ours: true }]
          .sort((a, b) => a.total - b.total)
          .map((r, i, all) => (
            <div
              key={r.name}
              className={`${LIST_ROW} -mx-3 flex-1 px-3 py-2 ${i < all.length - 1 ? "border-b border-slate-100" : ""}`}
              style={r.ours ? { backgroundColor: "rgba(0,62,86,0.04)" } : undefined}
            >
              <span className="flex min-w-0 items-center gap-2">
                {r.ours ? <Warehouse className="h-4 w-4 shrink-0 text-logo" /> : <OperatorMark name={r.name} color={r.color} />}
                <span className={`min-w-0 truncate text-sm text-slate-900 ${r.ours ? "font-semibold" : ""}`}>
                  {r.name}
                  {!r.ours && <span className="hidden text-slate-400 sm:inline"> · {terms(r)}</span>}
                </span>
              </span>
              <span className="hidden text-right text-sm tabular-nums text-slate-400 sm:block">${r.street}</span>
              <span className={`text-right text-sm tabular-nums text-slate-900 ${r.ours ? "font-semibold" : "font-medium"}`}>${r.total}</span>
              {r.ours ? (
                <span />
              ) : (
                <span className="justify-self-end px-1.5 text-xs font-medium tabular-nums text-slate-700" style={{ backgroundColor: gapTone(pctVs(OURS_TOTAL, r.total)) }}>
                  {signed(pctVs(OURS_TOTAL, r.total))}
                </span>
              )}
            </div>
          ))}
      </div>
      <p className="mt-2 hidden text-[11px] text-slate-400 sm:block">All-in is the average month of a six-month stay with promotions, fees, and insurance included.</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Catch the facility others missed: a market map with the one nobody else lists lit up.

const MAP_DOTS: [number, number, string][] = [[62, 40, "#06b6d4"], [118, 62, "#8b5cf6"], [196, 34, "#031332"], [232, 96, "#f97316"], [88, 118, "#22c55e"], [150, 132, "#8b5cf6"], [262, 52, "#06b6d4"]];

function MissedVisual() {
  return (
    <div>
      <svg viewBox="0 0 300 160" className="block h-auto w-full" aria-hidden>
        <rect width="300" height="160" fill="#eef3f6" />
        {/* Roads and a river, soft and wide. */}
        <path d="M0 92 C 80 84, 140 104, 300 88" fill="none" stroke="#ffffff" strokeWidth="9" strokeLinecap="round" />
        <path d="M128 0 C 134 60, 120 110, 140 160" fill="none" stroke="#ffffff" strokeWidth="7" strokeLinecap="round" />
        <path d="M300 128 C 250 120, 220 146, 180 160" fill="none" stroke="#d6e4ec" strokeWidth="10" strokeLinecap="round" />
        <circle cx="150" cy="80" r="70" fill="#003e56" fillOpacity="0.04" />
        {MAP_DOTS.map(([x, y, color]) => (
          <rect key={`${x},${y}`} x={x - 5} y={y - 5} width="10" height="10" fill="#ffffff" stroke={color} strokeWidth="2" />
        ))}
        {/* Ours, at the centre of its market. */}
        <polygon points={octagonPoints(150, 80, 8)} fill="#003e56" stroke="#eef3f6" strokeWidth="2" />
        {/* The one found. */}
        <rect x="192" y="108" width="28" height="28" fill={GOLD} fillOpacity="0.16" />
        <rect x="199" y="115" width="14" height="14" fill={GOLD} stroke="#eef3f6" strokeWidth="2" />
      </svg>
      <div className="mt-3 flex items-start gap-2 text-sm">
        <span className="mt-1.5 h-2.5 w-2.5 shrink-0" style={{ backgroundColor: GOLD }} />
        <span className="text-slate-700">
          <span className="font-medium text-slate-900">Westbrook Mini Storage</span> · 38,000 sq ft, two miles from you
        </span>
      </div>
    </div>
  );
}

function octagonPoints(cx: number, cy: number, r: number) {
  return Array.from({ length: 8 }, (_, i) => {
    const a = Math.PI / 8 + (i * Math.PI) / 4;
    return `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`;
  }).join(" ");
}

// ---------------------------------------------------------------------------
// Market supply, read from several signals together: space per person, how many facilities are
// discounting, and where rates have gone. Each cell leans toward too much space (gold) or too
// little (blue); the verdict weighs them all.

type Signal = { value: string; lean: number };
const SUPPLY: { market: string; verdict: string; lean: number; space: Signal; promos: Signal; rates: Signal }[] = [
  { market: "Mesa", verdict: "Over", lean: 16, space: { value: "9.8 sq ft", lean: 16 }, promos: { value: "7 of 9", lean: 16 }, rates: { value: "−6%", lean: 8 } },
  { market: "Frisco", verdict: "Balanced", lean: 0, space: { value: "7.1 sq ft", lean: 0 }, promos: { value: "3 of 8", lean: 0 }, rates: { value: "+1%", lean: 0 } },
  { market: "Boise", verdict: "Under", lean: -16, space: { value: "5.2 sq ft", lean: -16 }, promos: { value: "1 of 6", lean: -8 }, rates: { value: "+7%", lean: -16 } },
];

const SUPPLY_COLUMNS = ["Per person", "Promos", "Rates"];

function SupplyVisual() {
  const grid = "grid grid-cols-[minmax(0,1.15fr)_repeat(3,minmax(0,1fr))] gap-1";
  return (
    <div>
      <div className={`${grid} pb-1.5 text-[10px] font-medium uppercase tracking-wider text-slate-500`}>
        <span>Market</span>
        {SUPPLY_COLUMNS.map((c) => (
          <span key={c} className="text-center leading-tight">{c}</span>
        ))}
      </div>
      <div className="space-y-1">
        {SUPPLY.map((m) => (
          <div key={m.market} className={`${grid} items-stretch`}>
            <div className="min-w-0 py-1">
              <p className="truncate text-sm font-medium text-slate-900">{m.market}</p>
              <p className="mt-0.5 inline-block px-1 text-[11px] font-medium text-slate-700" style={{ backgroundColor: gapTone(m.lean) }}>{m.verdict}</p>
            </div>
            {[m.space, m.promos, m.rates].map((sig, j) => (
              <div key={j} className="flex items-center justify-center text-sm font-medium tabular-nums text-slate-800" style={{ backgroundColor: gapTone(sig.lean) }}>
                {sig.value}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Outperforming: priced above the market and still filling.

function OutperformVisual() {
  return (
    <div>
      <div className="flex items-baseline gap-2">
        <span className="text-[34px] font-semibold leading-none tabular-nums text-slate-900">+6%</span>
        <span className="text-sm text-slate-600">above the market median</span>
      </div>
      <div className="mt-4">
        <RangeLine low={152} high={205} median={176} ours={186} domain={[146, 211]} />
      </div>
      <div className="mt-1 flex justify-between text-[11px] tabular-nums text-slate-500">
        <span>$152</span>
        <span>Market range</span>
        <span>$205</span>
      </div>
      <div className="mt-4 flex items-center gap-2 border-t border-slate-100 pt-3 text-sm text-slate-700">
        <Check className="h-4 w-4 shrink-0 text-logo" />
        94% occupied against a market of 90%
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rate growth: two years of street rates, then the next six months projected with their range.

const HISTORY = [148, 149, 149, 151, 153, 155, 156, 155, 154, 154, 155, 157, 158, 160, 161, 163, 164, 163, 162, 162, 163, 164, 165, 166];
const PROJECTED = [167, 168, 169, 170, 170, 171];

function GrowthVisual() {
  const all = [...HISTORY, ...PROJECTED];
  const n = all.length - 1;
  const [lo, hi] = [140, 180];
  const x = (i: number) => (i / n) * 300;
  const y = (v: number) => 100 - ((v - lo) / (hi - lo)) * 100;
  const today = HISTORY.length - 1;
  const line = (from: number, values: number[]) => values.map((v, i) => `${i ? "L" : "M"}${x(from + i)},${y(v)}`).join(" ");
  const band = (k: number) => PROJECTED.map((v, i) => [x(today + 1 + i), v, (i + 1) * k] as const);
  const upper = band(0.9).map(([px, v, s]) => `${px},${y(v + s)}`);
  const lower = band(0.9).map(([px, v, s]) => `${px},${y(v - s)}`).reverse();
  const pct = (i: number, v: number) => ({ left: `${(x(i) / 300) * 100}%`, top: `${y(v)}%` });
  return (
    <div>
      <div className="relative h-36">
        <svg viewBox="0 0 300 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full overflow-visible" aria-hidden>
          {[150, 160, 170].map((v) => (
            <line key={v} x1="0" x2="300" y1={y(v)} y2={y(v)} stroke="#e2e8f0" strokeWidth="1" vectorEffect="non-scaling-stroke" />
          ))}
          <path d={`${line(0, HISTORY)} L${x(today)},100 L0,100 Z`} fill="#003e56" fillOpacity="0.08" />
          <polygon points={`${x(today)},${y(HISTORY[today]!)} ${upper.join(" ")} ${lower.join(" ")}`} fill="#003e56" fillOpacity="0.07" />
          <path d={line(0, HISTORY)} fill="none" stroke="#003e56" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
          <path d={line(today, [HISTORY[today]!, ...PROJECTED])} fill="none" stroke="#003e56" strokeWidth="2" strokeDasharray="4 4" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        </svg>
        {[150, 160, 170].map((v) => (
          <span key={v} className="absolute left-0 -translate-y-full pb-0.5 text-[10px] tabular-nums text-slate-400" style={{ top: `${y(v)}%` }}>${v}</span>
        ))}
        <span className="absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 bg-logo ring-2 ring-white" style={pct(today, HISTORY[today]!)} />
        <span className="absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 border-2 border-logo bg-white" style={pct(n, PROJECTED.at(-1)!)} />
        <span className="absolute -translate-x-full -translate-y-[170%] whitespace-nowrap pr-1 text-xs font-medium tabular-nums text-slate-900" style={pct(n, PROJECTED.at(-1)!)}>$171 projected</span>
      </div>
      <div className="relative mt-2 h-4 text-[11px] text-slate-500">
        <span className="absolute left-0">2 years ago</span>
        <span className="absolute -translate-x-1/2" style={{ left: `${(today / n) * 100}%` }}>Today</span>
        <span className="absolute right-0"><span className="sm:hidden">+6 mo</span><span className="hidden sm:inline">In 6 months</span></span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rate change check: weekly move-ins before and after a change.

const BEFORE = [8, 9, 7, 8, 9, 8, 7, 9];
const AFTER = [8, 7, 8, 8, 7, 9];

function ChangeVisual() {
  const max = 12;
  const avg = (v: number[]) => (v.reduce((a, b) => a + b, 0) / v.length).toFixed(1);
  return (
    <div>
      <div className="relative flex h-28 items-end gap-[2px] border-b border-slate-200">
        {[...BEFORE, ...AFTER].map((v, i) => (
          <div key={i} className="flex flex-1 justify-center">
            <div className={`w-full max-w-[24px] ${i < BEFORE.length ? "bg-slate-300" : "bg-logo"}`} style={{ height: `${(v / max) * 112}px` }} />
          </div>
        ))}
        <div className="absolute bottom-0 top-0 w-px bg-slate-400" style={{ left: `${(BEFORE.length / (BEFORE.length + AFTER.length)) * 100}%` }}>
          <span className="absolute -top-1 left-1.5 whitespace-nowrap bg-white px-1 text-[11px] font-medium text-slate-900">+$10 on Mar 3</span>
        </div>
      </div>
      <div className="mt-2 flex justify-between text-[11px] text-slate-500">
        <span>{avg(BEFORE)} move-ins a week</span>
        <span>{avg(AFTER)} a week</span>
      </div>
      <div className="mt-3 flex items-center gap-2 text-sm text-slate-700">
        <Check className="h-4 w-4 shrink-0 text-logo" />
        Holding: move-ins steady since the change
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tenant rate increases: extra revenue a month, after move-outs, by the size of the increase.

function IncreaseVisual() {
  const peak = 9;
  const top = 4120;
  const gain = (pct: number) => top * (1 - ((pct - peak) / peak) ** 2);
  const pts = Array.from({ length: 41 }, (_, i) => i / 2);
  const [lo, hi] = [-2500, 4800];
  const x = (p: number) => (p / 20) * 300;
  const y = (v: number) => 100 - ((v - lo) / (hi - lo)) * 100;
  const curve = pts.map((p, i) => `${i ? "L" : "M"}${x(p)},${y(gain(p))}`).join(" ");
  const at = { left: `${(x(peak) / 300) * 100}%`, top: `${y(top)}%` };
  return (
    <div>
      <div className="relative h-36">
        <svg viewBox="0 0 300 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full overflow-visible" aria-hidden>
          <line x1="0" x2="300" y1={y(0)} y2={y(0)} stroke="#cbd5e1" strokeWidth="1" vectorEffect="non-scaling-stroke" />
          <line x1={x(peak)} x2={x(peak)} y1={y(0)} y2={y(top)} stroke="#cbd5e1" strokeWidth="1" vectorEffect="non-scaling-stroke" />
          <path d={`${pts.filter((p) => p <= 2 * peak).map((p, i) => `${i ? "L" : "M"}${x(p)},${y(gain(p))}`).join(" ")} Z`} fill="#003e56" fillOpacity="0.08" />
          <path d={curve} fill="none" stroke="#003e56" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        </svg>
        <span className="absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 bg-logo ring-2 ring-white" style={at} />
        <span className="absolute -translate-y-[160%] translate-x-3 whitespace-nowrap text-xs font-medium tabular-nums text-slate-900" style={at}>
          9%: +$4,120 a month
        </span>
      </div>
      <div className="relative mt-2 h-4 text-[11px] tabular-nums text-slate-500">
        <span className="absolute left-0">0%</span>
        <span className="absolute -translate-x-1/2" style={{ left: "50%" }}>10%</span>
        <span className="absolute right-0">20% increase</span>
      </div>
      <p className="mt-3 text-[11px] text-slate-500">Move-outs cost more than any increase above 18% brings in.</p>
    </div>
  );
}

// ---------------------------------------------------------------------------

/** Tiles under `ref` rise in as they come into view. */
export function useReveal(ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const tiles = Array.from(ref.current?.querySelectorAll<HTMLElement>(".bento-tile") ?? []);
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          e.target.setAttribute("data-shown", "");
          observer.unobserve(e.target);
        }
      },
      { rootMargin: "0px 0px -10% 0px" },
    );
    tiles.forEach((t) => observer.observe(t));
    return () => observer.disconnect();
  }, [ref]);
}

export const REVEAL_STYLES = `
  .bento-tile { opacity: 0; transform: translateY(14px); transition: opacity 600ms cubic-bezier(0.2, 0.8, 0.2, 1), transform 600ms cubic-bezier(0.2, 0.8, 0.2, 1); }
  .bento-tile[data-shown] { opacity: 1; transform: none; }
  @media (prefers-reduced-motion) { .bento-tile { opacity: 1; transform: none; transition: none; } }
`;

export function Bento() {
  const ref = useRef<HTMLDivElement>(null);
  useReveal(ref);

  let i = 0;
  return (
    <section id="features" className="scroll-mt-16 bg-[#f4f7f9] py-24">
      <style>{REVEAL_STYLES}</style>
      <div ref={ref} className="mx-auto max-w-6xl px-5">
        <h2 className="max-w-2xl text-balance text-[clamp(32px,5vw,48px)] font-semibold leading-[1.05] tracking-[-0.03em] text-logo">
          Know your market before you set a rate.
        </h2>
        <p className="mt-4 max-w-xl text-pretty text-lg leading-relaxed text-slate-600">
          From your competitors to the tenants already renting with you.
        </p>
        <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-6">
          <Tile id="rates" index={i++} title="What a tenant really pays down the street." desc="Promotions, fees, and insurance included." className="md:col-span-2 lg:col-span-4 lg:row-span-2">
            <RatesVisual />
          </Tile>
          <Tile index={i++} title="Catch the facility others missed." desc="Every facility in your market, down to the independents." className="lg:col-span-2">
            <MissedVisual />
          </Tile>
          <Tile id="markets" index={i++} title="Know which markets are over- and under-supplied." desc="Square feet per person is only the start." className="lg:col-span-2">
            <SupplyVisual />
          </Tile>
          <Tile index={i++} title="See where your rates outperform." desc="Above the market and still filling." className="lg:col-span-2">
            <OutperformVisual />
          </Tile>
          <Tile index={i++} title="Past and projected rate growth." desc="Street rates over time and where they're heading." className="lg:col-span-4">
            <GrowthVisual />
          </Tile>
          <Tile index={i++} title="Know how a rate change is doing." desc="Move-ins before and after every change." className="lg:col-span-2">
            <ChangeVisual />
          </Tile>
          <Tile id="revenue" index={i++} title="Raise tenant rates by the right amount." desc="The increase that earns the most once move-outs are counted." className="md:col-span-2 lg:col-span-4">
            <IncreaseVisual />
          </Tile>
        </div>
      </div>
    </section>
  );
}
